import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useUserStore } from '@/store/useUserStore';
import { cn } from '@/lib/utils';

const ENV_STYLES: Record<string, { ribbon: string; panelHeader: string; chip: string; hex: string }> = {
  local: {
    ribbon: 'bg-emerald-500/55 text-white',
    panelHeader: 'bg-emerald-500 text-white',
    chip: 'bg-emerald-100 text-emerald-800',
    hex: '#10b981',
  },
  dev: {
    ribbon: 'bg-sky-500/55 text-white',
    panelHeader: 'bg-sky-500 text-white',
    chip: 'bg-sky-100 text-sky-800',
    hex: '#0ea5e9',
  },
  development: {
    ribbon: 'bg-sky-500/55 text-white',
    panelHeader: 'bg-sky-500 text-white',
    chip: 'bg-sky-100 text-sky-800',
    hex: '#0ea5e9',
  },
  qa: {
    ribbon: 'bg-amber-500/55 text-white',
    panelHeader: 'bg-amber-500 text-white',
    chip: 'bg-amber-100 text-amber-800',
    hex: '#f59e0b',
  },
  uat: {
    ribbon: 'bg-orange-500/55 text-white',
    panelHeader: 'bg-orange-500 text-white',
    chip: 'bg-orange-100 text-orange-800',
    hex: '#f97316',
  },
  staging: {
    ribbon: 'bg-orange-500/55 text-white',
    panelHeader: 'bg-orange-500 text-white',
    chip: 'bg-orange-100 text-orange-800',
    hex: '#f97316',
  },
};

const DETAIL_ENVS = new Set(['local', 'qa']);
const PROD_ENVS = new Set(['production', 'prod', '']);

// Persisted flag that hides the ribbon for clean screenshots. Toggle it with a
// URL param (?hideEnvRibbon=1 to hide, =0 to restore) — the choice sticks in
// localStorage so it survives in-app navigation and reloads while capturing.
const RIBBON_HIDE_KEY = 'hideEnvRibbon';

export const EnvIndicator = () => {
  const env = String(import.meta.env.VITE_APP_ENV ?? '').toLowerCase();
  const isProd = PROD_ENVS.has(env);

  // Screenshot mode: suppress the ribbon (and its mobile pill) entirely. The
  // URL param wins over the stored flag on this render; the effect below then
  // persists that choice so it survives navigation/reloads while capturing.
  const [ribbonHidden] = useState(() => {
    try {
      const param = new URLSearchParams(window.location.search).get(RIBBON_HIDE_KEY);
      if (param === '0') return false;
      if (param != null) return true;
      return localStorage.getItem(RIBBON_HIDE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      const param = new URLSearchParams(window.location.search).get(RIBBON_HIDE_KEY);
      if (param == null) return;
      if (param === '0') localStorage.removeItem(RIBBON_HIDE_KEY);
      else localStorage.setItem(RIBBON_HIDE_KEY, '1');
    } catch {
      /* ignore storage failures (private mode etc.) */
    }
  }, []);

  const user = useUserStore((s) => s.user);
  const role = useUserStore((s) => s.role);
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin);
  const selectedWorkspaceId = useUserStore((s) => s.selectedWorkspaceId);
  const workspaces = useUserStore((s) => s.workspaces);

  const [open, setOpen] = useState(false);

  const showDetails = DETAIL_ENVS.has(env);
  const workspace = workspaces.find((w) => w.id === selectedWorkspaceId);

  const info: Record<string, unknown> = {
    env,
    appName: import.meta.env.VITE_APP_NAME,
    appUrl: import.meta.env.VITE_APP_URL,
    apiUrl: import.meta.env.VITE_API_URL,
  };

  if (showDetails) {
    info.user = user
      ? {
          id: user.user_id,
          name: user.user?.name,
          email: user.user?.email,
        }
      : null;
    info.role = role;
    info.isSuperAdmin = isSuperAdmin;
    info.roles = user?.roles ?? null;
    info.permissionsCount = (user?.permissions || []).length;
    info.workspace = {
      id: selectedWorkspaceId,
      name: workspace?.name ?? null,
    };
  }

  const copyText = JSON.stringify(info, null, 2);

  useEffect(() => {
    if (isProd) return;
    const style = ENV_STYLES[env] ?? { hex: '#6b7280' };
    console.log(
      `%c ${env.toUpperCase()} %c env indicator`,
      `background:${style.hex};color:white;font-weight:700;padding:2px 6px;border-radius:3px`,
      'color:inherit;font-weight:500;margin-left:6px',
      info,
    );
  }, [env, isProd, user?.user_id, selectedWorkspaceId, role, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isProd || ribbonHidden) return null;

  const style = ENV_STYLES[env] ?? {
    ribbon: 'bg-gray-600/55 text-white',
    panelHeader: 'bg-gray-600 text-white',
    chip: 'bg-gray-200 text-gray-800',
    hex: '#6b7280',
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      toast.success(`Copied ${env.toUpperCase()} info`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <>
      {/* Desktop: diagonal corner ribbon (hidden on mobile, where it overlaps
          the top-left navigation toggle). */}
      <div className="hidden sm:block fixed top-0 left-0 z-[10000] w-[110px] h-[110px] overflow-hidden pointer-events-none">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title={`${env.toUpperCase()} — click for details`}
          className={cn(
            'pointer-events-auto absolute',
            'left-[-44px] top-[22px]',
            'w-[180px] py-[3px]',
            'text-[10px] font-bold tracking-[0.15em] uppercase text-center',
            'shadow-md cursor-pointer backdrop-blur-sm',
            'transform -rotate-45 origin-center',
            'hover:bg-opacity-90 hover:brightness-110 transition',
            style.ribbon,
          )}
        >
          {env}
        </button>
      </div>

      {/* Mobile: compact centered pill pinned to the very top, clear of the
          top-left toggle so the sidebar expand button stays tappable. */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`${env.toUpperCase()} — tap for details`}
        className={cn(
          'sm:hidden fixed top-0.5 left-1/2 -translate-x-1/2 z-[10000]',
          'px-3 py-[3px] rounded-b-md',
          'text-[10px] font-bold tracking-[0.15em] uppercase text-center',
          'shadow-md cursor-pointer backdrop-blur-sm',
          'hover:brightness-110 transition',
          style.ribbon,
        )}
      >
        {env}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[10000] bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div className="fixed top-[120px] left-3 z-[10001] w-[340px] max-w-[calc(100vw-1.5rem)] rounded-md border border-gray-200 bg-white shadow-2xl text-xs overflow-hidden">
            <div
              className={cn(
                'flex items-center justify-between px-3 py-2 font-semibold',
                style.panelHeader,
              )}
            >
              <span className="uppercase tracking-wider">
                {env} environment
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white text-base leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-3 py-2 space-y-1.5 text-gray-800">
              <Row label="App">{String(info.appName ?? '—')}</Row>
              <Row label="API">{String(info.apiUrl ?? '—')}</Row>
              {showDetails && (
                <>
                  <div className="my-2 h-px bg-gray-100" />
                  <Row label="User">
                    {user
                      ? `${user.user?.name ?? '—'} (#${user.user_id})`
                      : '— not signed in —'}
                  </Row>
                  {user?.user?.email && (
                    <Row label="Email">{user.user.email}</Row>
                  )}
                  <Row label="Role">
                    <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase', style.chip)}>
                      {role ?? '—'}
                    </span>
                    {isSuperAdmin && (
                      <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-purple-100 text-purple-800">
                        super
                      </span>
                    )}
                  </Row>
                  {user?.roles && user.roles.length > 0 && (
                    <Row label="Roles">{user.roles.join(', ')}</Row>
                  )}
                  <Row label="Workspace">
                    {selectedWorkspaceId
                      ? `${workspace?.name ?? '?'} (#${selectedWorkspaceId})`
                      : '—'}
                  </Row>
                  <Row label="Perms">{(user?.permissions || []).length}</Row>
                </>
              )}
            </div>

            <div className="flex justify-between items-center px-3 py-2 border-t border-gray-100 bg-gray-50">
              <span className="text-[10px] text-gray-500">
                Click copy to send full payload to clipboard
              </span>
              <button
                type="button"
                onClick={handleCopy}
                className="px-2.5 py-1 text-[11px] font-semibold rounded bg-gray-900 text-white hover:bg-gray-700"
              >
                Copy
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-2">
    <span className="w-16 shrink-0 text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
      {label}
    </span>
    <span className="flex-1 break-all">{children}</span>
  </div>
);
